import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { useRole } from 'context/RoleContext';
import { THREADS, ANNOUNCEMENTS, DEAL_ROOMS, formatRelativeTime, getParticipant } from './communicationsData';

const PRIORITY_CONFIG = {
  HIGH: { label: 'High', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  MEDIUM: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  LOW: { label: 'Low', bg: 'bg-primary/8', text: 'text-primary', border: 'border-primary/20', dot: 'bg-primary/40' },
};

function Avatar({ participant, size = 28 }) {
  return (
    <div
      className="flex-shrink-0 flex items-center justify-center rounded-full text-white font-semibold select-none"
      style={{ width: size, height: size, background: participant?.color || '#3b4c4c', fontSize: size * 0.36 }}
      title={participant?.name}
    >
      {participant?.initials}
    </div>
  );
}

function ThreadItem({ thread, isActive, onClick }) {
  const mainParticipants = thread?.participants?.slice(0, 3)?.map(id => getParticipant(id));
  const hasUnread = thread?.unreadCount > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 card-interactive group ${
        isActive
          ? 'bg-primary/8 border border-primary/15'
          : 'hover:bg-muted border border-transparent'
      }`}
    >
      <div className="flex items-start gap-2.5">
        {/* Avatar stack */}
        <div className="flex-shrink-0 relative" style={{ width: 32, height: 32 }}>
          {thread?.type === 'direct' ? (
            <Avatar participant={mainParticipants?.[1] || mainParticipants?.[0]} size={32} />
          ) : (
            <div className="relative">
              <Avatar participant={mainParticipants?.[0]} size={24} />
              {mainParticipants?.[1] && (
                <div className="absolute -bottom-1 -right-1">
                  <Avatar participant={mainParticipants?.[1]} size={18} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1 mb-0.5">
            <span className={`font-caption text-sm truncate ${hasUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
              {thread?.name}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {thread?.pinned && <Icon name="Pin" size={11} color="var(--color-muted-foreground)" />}
              <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">{formatRelativeTime(thread?.lastMessageTime)}</span>
            </div>
          </div>

          {thread?.dealLinked && (
            <div className="mb-1">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-primary/8 text-primary">
                <Icon name="Link2" size={9} />
                {thread?.dealLinked}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-1">
            <p className={`font-caption text-xs truncate ${hasUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
              {thread?.lastMessage}
            </p>
            {hasUnread && (
              <span className="flex-shrink-0 flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-xs font-semibold">
                {thread?.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function AnnouncementItem({ ann, isActive, onClick }) {
  const cfg = PRIORITY_CONFIG?.[ann?.priority];
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 card-interactive border ${
        isActive ? 'bg-primary/8 border-primary/15' : `hover:bg-muted ${cfg?.border} border-opacity-40`
      }`}
    >
      <div className="flex items-start gap-2">
        <div className={`flex-shrink-0 mt-1 w-2 h-2 rounded-full ${cfg?.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1 mb-0.5">
            <span className={`font-caption text-sm font-semibold truncate ${ann?.unread ? 'text-foreground' : 'text-foreground'}`}>
              {ann?.title}
            </span>
            {ann?.unread && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5" />}
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-caption text-xs font-medium px-1.5 py-0.5 rounded ${cfg?.bg} ${cfg?.text}`}>{cfg?.label}</span>
            <span className="font-caption text-xs text-muted-foreground">{formatRelativeTime(ann?.date)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function DealRoomItem({ room, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 card-interactive border ${
        isActive ? 'bg-primary/8 border-primary/15' : 'hover:bg-muted border-transparent'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Hash" size={16} color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-caption text-sm font-medium text-foreground truncate">{room?.name}</span>
            <span className="font-caption text-xs text-muted-foreground flex-shrink-0">{formatRelativeTime(room?.lastActivity)}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-caption text-xs text-muted-foreground flex items-center gap-1">
              <Icon name="Users" size={10} />
              {room?.participants} participants
            </span>
            <span className="font-caption text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{room?.stage}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function LeftPanel({ activeThreadId, onSelectThread, onSelectAnnouncement, onSelectDealRoom, activeAnnId, activeDealRoomId, onCompose }) {
  const { role, activeContext } = useRole();
  const [tab, setTab] = useState('threads');
  const [search, setSearch] = useState('');

  const scopeFilter = (item) => {
    if (item?.roleTarget && !item?.roleTarget?.includes(role)) return false;
    if (item?.participantContextIds && !item?.participantContextIds?.includes(activeContext?.id)) return false;
    return true;
  };

  const scopedThreads = THREADS?.filter(scopeFilter);
  const scopedAnnouncements = ANNOUNCEMENTS?.filter((item) => {
    if (item?.roleTarget && !item?.roleTarget?.includes(role)) return false;
    return true;
  });
  const scopedDealRooms = DEAL_ROOMS?.filter(scopeFilter);

  const filteredThreads = scopedThreads?.filter(t =>
    t?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (t?.dealLinked || '')?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const totalUnread = scopedThreads?.reduce((sum, t) => sum + t?.unreadCount, 0);

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-base font-semibold text-foreground">Messages</h2>
            {totalUnread > 0 && (
              <span className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-xs font-semibold">
                {totalUnread}
              </span>
            )}
          </div>
          <button
            onClick={onCompose}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="PenSquare" size={13} />
            Compose
          </button>
        </div>
        <div className="relative">
          <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search conversations…"
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-8 pr-3 py-1.5 font-caption text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
          />
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-1 px-3 py-2 border-b border-border">
        {[{ id: 'threads', label: 'Threads' }, { id: 'announcements', label: 'Announcements' }, { id: 'dealrooms', label: 'Deal Rooms' }]?.map(t => (
          <button
            key={t?.id}
            onClick={() => setTab(t?.id)}
            className={`flex-1 py-1 px-1.5 rounded-md font-caption text-xs font-medium transition-all ${
              tab === t?.id ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {t?.label}
          </button>
        ))}
      </div>
      {/* List */}
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {tab === 'threads' && (
          filteredThreads?.length > 0 ? (
            filteredThreads?.map(thread => (
              <ThreadItem
                key={thread?.id}
                thread={thread}
                isActive={activeThreadId === thread?.id}
                onClick={() => onSelectThread(thread)}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="MessageSquare" size={32} color="var(--color-muted-foreground)" className="mb-2" />
              <p className="font-caption text-sm text-muted-foreground">No conversations match your search</p>
            </div>
          )
        )}

        {tab === 'announcements' && scopedAnnouncements?.map(ann => (
          <AnnouncementItem
            key={ann?.id}
            ann={ann}
            isActive={activeAnnId === ann?.id}
            onClick={() => onSelectAnnouncement(ann)}
          />
        ))}

        {tab === 'dealrooms' && scopedDealRooms?.map(room => (
          <DealRoomItem
            key={room?.id}
            room={room}
            isActive={activeDealRoomId === room?.id}
            onClick={() => onSelectDealRoom(room)}
          />
        ))}
      </div>
    </div>
  );
}
